import { useState } from "react";
import Bugsnag from "@bugsnag/js";
import { Loader2, Pencil } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { normalizeDistrictDomain } from "@/lib/districtDomain";
import { updateDistrict } from "@/services/systemAdminService";
import type { LicenseInventoryRow } from "@/types/systemAdmin";

interface EditDistrictDialogProps {
  district: LicenseInventoryRow;
  onUpdated: () => Promise<void>;
}

export const EditDistrictDialog = ({ district, onUpdated }: EditDistrictDialogProps) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(district.districtName);
  const [domain, setDomain] = useState(district.districtDomain ?? "");
  const [domainError, setDomainError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const openDialog = () => {
    setName(district.districtName);
    setDomain(district.districtDomain ?? "");
    setDomainError(null);
    setOpen(true);
  };

  const handleSave = async () => {
    if (!name.trim()) return;

    const { domain: normalized, error: validationError } = normalizeDistrictDomain(domain);
    if (!normalized) {
      setDomainError(validationError);
      return;
    }

    setDomainError(null);
    setBusy(true);
    try {
      await updateDistrict(district.districtId, name, normalized);
      toast({ title: "District updated" });
      setOpen(false);
      await onUpdated();
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Unknown error");
      const description = error.message;
      const isDuplicateDomain = /duplicate.*domain|district.*domain.*exists/i.test(description);
      const errorMessage = isDuplicateDomain
        ? "That domain is already assigned to another district."
        : description;
      if (!isDuplicateDomain) {
        Bugsnag.notify(error, (event) => {
          event.severity = "warning";
        });
      }
      setDomainError(errorMessage);
      toast({
        title: "Could not update district",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={`Edit ${district.districtName}`}
        onClick={openDialog}
      >
        <Pencil className="h-4 w-4" aria-hidden />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit district</DialogTitle>
            <DialogDescription>
              Update this district&apos;s name and email domain.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-2">
              <label htmlFor="edit-district-name" className="text-sm text-gray-600">
                Name
              </label>
              <Input
                id="edit-district-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="edit-district-domain" className="text-sm text-gray-600">
                Domain
              </label>
              <Input
                id="edit-district-domain"
                value={domain}
                onChange={(e) => {
                  setDomain(e.target.value);
                  setDomainError(null);
                }}
                placeholder="District email domain (for example, d105.net)"
                aria-describedby={domainError ? "edit-district-domain-error" : undefined}
                aria-invalid={Boolean(domainError)}
              />
              {domainError ? (
                <p id="edit-district-domain-error" role="alert" className="text-sm text-red-600">
                  {domainError}
                </p>
              ) : null}
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              disabled={busy || !name.trim()}
              onClick={() => void handleSave()}
            >
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  Saving…
                </>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
